import { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import SEOMeta, { SchemaOrg } from "@/components/feature/SEOMeta";
import Breadcrumb, { getCategoryBreadcrumb } from "@/components/feature/Breadcrumb";
import { categories, articles } from "@/mocks/data";

export default function Category() {
  const { slug } = useParams<{ slug?: string }>();
  const [activeCategory, setActiveCategory] = useState<string>(slug || "all");

  const filteredArticles = useMemo(() => {
    if (activeCategory === "all") return articles;
    return articles.filter((a) => a.categoryId === activeCategory);
  }, [activeCategory]);

  const currentCategory = categories.find((c) => c.id === activeCategory);
  const pageTitle = currentCategory ? `${currentCategory.name} - 9170登录手机版入口` : "分类浏览 - 9170登录手机版入口";

  return (
    <Layout>
      <SEOMeta
        title={pageTitle}
        description={currentCategory ? currentCategory.description : "浏览9170手机版全部文章分类，包括使用指南、常见问题、最新动态、安全指南等，快速找到您需要的内容。"}
        keywords={currentCategory ? `${currentCategory.name},9170登录手机版入口,9170手机版登录` : "9170登录手机版入口,9170手机版登录,9170移动端入口,分类浏览"}
        canonicalUrl={`/category/${activeCategory !== "all" ? activeCategory : ""}`}
      />
      <SchemaOrg
        data={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: currentCategory ? currentCategory.name : "文章分类",
          description: currentCategory?.description || "9170手机版文章分类浏览",
          url: `https://9170.com/category/${activeCategory !== "all" ? activeCategory : ""}`,
        }}
      />
      <SchemaOrg
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://9170.com/" },
            { "@type": "ListItem", position: 2, name: "分类浏览", item: "https://9170.com/category" },
            ...(currentCategory
              ? [{ "@type": "ListItem", position: 3, name: currentCategory.name, item: `https://9170.com/category/${currentCategory.id}` }]
              : []),
          ],
        }}
      />

      <section className="pt-24 pb-16 md:pb-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Breadcrumb items={getCategoryBreadcrumb(currentCategory?.id)} />
          </div>

          <div className="mb-10">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: "var(--font-heading)" }}>
              {currentCategory ? currentCategory.name : "文章分类浏览"}
            </h1>
            <p className="text-sm text-foreground-600">
              {currentCategory
                ? currentCategory.description
                : "浏览9170手机版全部文章，涵盖使用指南、常见问题、最新动态、安全指南等主题"}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-10">
            <button
              onClick={() => setActiveCategory("all")}
              className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                activeCategory === "all"
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              全部文章
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  activeCategory === cat.id
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </button>
            ))}
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredArticles.map((article) => {
                const cat = categories.find((c) => c.id === article.categoryId);
                return (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="bg-background-100/60 rounded-lg p-5 hover:bg-background-100 transition-colors duration-200 group border border-background-200/50"
                  >
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      {cat && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
                          {cat.name}
                        </span>
                      )}
                      {article.tags.slice(0, 1).map((tag) => (
                        <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                      {article.summary}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span>{article.publishDate}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {article.readCount.toLocaleString()}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-600 text-sm">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}